import React, { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";
import {
  Search, MapPin, Clock, Star, Heart, MessageCircle,
  Phone, ChevronRight, X, AlertTriangle,
  Plus, Eye, Bookmark, Calendar, Package, Car, Book,
  Gift, School, Check, TrendingUp,
  Users, BookOpen, GraduationCap, Sparkles, BadgeCheck,
  ArrowRight, Shield,
  ShirtIcon, Laptop, Trophy, Headphones,
  Flame, HeartHandshake, Zap,
} from "lucide-react";

// ═══════════════════════════════════════════════════════════════════════════
// DESIGN TOKENS — Apple system palette (matches parent-dashboard tokens)
// ═══════════════════════════════════════════════════════════════════════════
const C = {
  text: "#1C1917",
  textMid: "#3C3530",
  textMuted: "#8B6F47",
  textLight: "#A68A5A",
  bg: "#EDE4D4",
  card: "#FFFFFF",
  border: "#E2D6C0",
  accent: "#7B1416",
  accentLight: "#F5EFE4",
  green: "#C4A882",
  greenLight: "#E3F9E5",
  red: "#B33A1A",
  redLight: "#FEE5E5",
  orange: "#C4A882",
  orangeLight: "#FFF4E5",
  teal: "#C4A882",
  tealLight: "#E6FFFE",
  purple: "#8B6F47",
  purpleLight: "#F5E6FD",
  pink: "#FF2D55",
  pinkLight: "#FFE8ED",
  gold: "#FFCC00",
  goldLight: "#FFFADC",
  blue: "#7B1416",
  blueLight: "#F5EFE4",
} as const;

// ═══════════════════════════════════════════════════════════════════════════
// CATEGORIES — floating intelligent chips
// ═══════════════════════════════════════════════════════════════════════════
type Category = {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  color: string;
  bg: string;
};

const CATEGORIES: Category[] = [
  { id: "books", label: "Textbooks", icon: BookOpen, color: C.purple, bg: C.purpleLight },
  { id: "uniforms", label: "Uniforms", icon: ShirtIcon, color: C.blue, bg: C.blueLight },
  { id: "tutoring", label: "Tutors", icon: GraduationCap, color: C.teal, bg: C.tealLight },
  { id: "events", label: "Events", icon: Calendar, color: C.pink, bg: C.pinkLight },
  { id: "transport", label: "Transport", icon: Car, color: C.green, bg: C.greenLight },
  { id: "community", label: "Donations", icon: Gift, color: C.green, bg: C.greenLight },
  { id: "lost", label: "Lost & Found", icon: AlertTriangle, color: C.red, bg: C.redLight },
  { id: "services", label: "Services", icon: Sparkles, color: C.orange, bg: C.orangeLight },
  { id: "instruments", label: "Music", icon: Headphones, color: C.pink, bg: C.pinkLight },
  { id: "sports", label: "Sports", icon: Trophy, color: C.orange, bg: C.orangeLight },
  { id: "electronics", label: "Technology", icon: Laptop, color: C.accent, bg: C.accentLight },
  { id: "supplies", label: "Supplies", icon: Book, color: C.green, bg: C.greenLight },
];

// ═══════════════════════════════════════════════════════════════════════════
// LISTING TYPE
// ═══════════════════════════════════════════════════════════════════════════
interface Listing {
  id: string;
  title: string;
  description?: string;
  price?: number;
  price_unit?: string;
  category: string;
  condition?: string;
  location?: string;
  school_id?: string;
  schools?: { name: string };
  posted_by?: string;
  posted_by_role: string;
  posted_by_name?: string;
  contact_phone?: string;
  contact_email?: string;
  image_urls?: string[];
  featured?: boolean;
  urgent?: boolean;
  verified?: boolean;
  views?: number;
  saves?: number;
  status?: string;
  tags?: string[];
  cross_campus?: boolean;
  created_at: string;
  trust_score?: number;
  school_badge?: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// SEASONAL CONTEXT
// ═══════════════════════════════════════════════════════════════════════════
function getSeasonalContext(): { label: string; highlights: string[] } {
  const month = new Date().getMonth();
  if (month <= 1) return { label: "Start of Year", highlights: ["School supplies", "Admissions", "Uniforms", "Books"] };
  if (month <= 3) return { label: "Term 1", highlights: ["Sports", "Activities", "Tutors", "Science fairs"] };
  if (month <= 6) return { label: "Mid-Year", highlights: ["Revision", "Exam prep", "Science kits", "Tutors"] };
  return { label: "Year End", highlights: ["Holiday camps", "Donations", "Uniform exchanges", "Community drives"] };
}

// ═══════════════════════════════════════════════════════════════════════════
// DISCOVERY CARD — Pinterest-style varying heights
// ═══════════════════════════════════════════════════════════════════════════
function DiscoveryCard({
  listing,
  index,
  onSelect,
  saved,
  onToggleSave,
}: {
  listing: Listing;
  index: number;
  onSelect: () => void;
  saved: boolean;
  onToggleSave: () => void;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const cat = CATEGORIES.find((c) => c.id === listing.category) ?? CATEGORIES[0];
  const Icon = cat.icon;
  const imageUrl = listing.image_urls?.[0];
  const hasImage = !!imageUrl;

  // Vary card heights for Pinterest feel
  const heightVariant = (index % 3) as 0 | 1 | 2;
  const imageHeight = hasImage
    ? heightVariant === 0
      ? "h-56"
      : heightVariant === 1
        ? "h-44"
        : "h-64"
    : "h-32";

  const timeAgo = useMemo(() => {
    const diff = Math.floor((new Date().getTime() - new Date(listing.created_at).getTime()) / (1000 * 60 * 60 * 24));
    if (diff === 0) return "Today";
    if (diff === 1) return "Yesterday";
    if (diff < 7) return `${diff}d ago`;
    if (diff < 30) return `${Math.floor(diff / 7)}w ago`;
    return `${Math.floor(diff / 30)}mo ago`;
  }, [listing.created_at]);

  const formatPrice = (price?: number, unit?: string) => {
    if (!price) return "Free";
    return `${price.toLocaleString()} ${unit || "UGX"}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.04, 0.4), duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      whileHover={{ y: -4 }}
      className="group break-inside-avoid mb-4 cursor-pointer"
      onClick={onSelect}
    >
      <div
        className="relative overflow-hidden rounded-[20px] border border-[#E2D6C0]/50 shadow-premium transition-all"
        style={{
          background: C.card,
          boxShadow: isHovered
            ? "0 12px 32px -8px rgba(0,0,0,0.12), 0 0 0 0.5px rgba(0,0,0,0.04)"
            : "0 1px 3px rgba(0,0,0,0.04), 0 8px 24px -6px rgba(0,0,0,0.06)",
        }}
      >
        {/* Image or illustration */}
        <div className={`relative ${imageHeight} overflow-hidden`}>
          {hasImage ? (
            <motion.img
              src={imageUrl}
              alt={listing.title}
              className="w-full h-full object-cover"
              animate={{ scale: isHovered ? 1.06 : 1 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
          ) : (
            <div
              className="h-full flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${cat.bg} 0%, ${cat.color}15 100%)` }}
            >
              <Icon className="h-10 w-10" style={{ color: cat.color, opacity: 0.5 }} />
            </div>
          )}

          {/* Top overlay */}
          <div className="absolute top-2.5 left-2.5 right-2.5 flex items-start justify-between">
            <div className="flex gap-1.5">
              {listing.urgent && (
                <span className="px-2 py-0.5 rounded-lg text-[10px] font-semibold text-white flex items-center gap-0.5" style={{ background: C.red }}>
                  <AlertTriangle className="w-2.5 h-2.5" /> Urgent
                </span>
              )}
              {listing.featured && !listing.urgent && (
                <span className="px-2 py-0.5 rounded-lg text-[10px] font-semibold text-white flex items-center gap-0.5" style={{ background: C.accent }}>
                  <Sparkles className="w-2.5 h-2.5" /> Featured
                </span>
              )}
            </div>
            <motion.button
              whileTap={{ scale: 0.85 }}
              onClick={(e) => { e.stopPropagation(); onToggleSave(); }}
              className="p-2 rounded-full backdrop-blur-md transition-all"
              style={{ background: saved ? C.red : "rgba(255,255,255,0.85)" }}
            >
              <Heart className="w-3.5 h-3.5" style={{ color: saved ? "white" : C.textMuted }} fill={saved ? "white" : "none"} />
            </motion.button>
          </div>

          {/* Price chip */}
          {listing.price != null && (
            <div className="absolute bottom-2.5 left-2.5">
              <span className="px-2.5 py-1 rounded-lg text-xs font-semibold backdrop-blur-md" style={{ background: "rgba(0,0,0,0.65)", color: "white" }}>
                {formatPrice(listing.price, listing.price_unit)}
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-3.5">
          <h3 className="text-sm font-semibold tracking-tight line-clamp-2 leading-snug mb-2" style={{ color: C.text }}>
            {listing.title}
          </h3>

          {/* Seller row */}
          <div className="flex items-center gap-2 mb-2.5">
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold text-white shrink-0"
              style={{ background: `linear-gradient(135deg, ${cat.color}, ${cat.color}cc)` }}
            >
              {listing.posted_by_name?.charAt(0) || "P"}
            </div>
            <span className="text-[11px] font-medium truncate" style={{ color: C.textMid }}>
              {listing.posted_by_name || "Parent"}
            </span>
            {listing.verified && <BadgeCheck className="w-3 h-3 shrink-0" style={{ color: C.green }} />}
            {listing.school_badge && <Shield className="w-3 h-3 shrink-0" style={{ color: C.accent }} />}
            {listing.trust_score != null && listing.trust_score >= 4.5 && (
              <div className="flex items-center gap-0.5 ml-auto shrink-0">
                <Star className="w-2.5 h-2.5" style={{ color: C.gold }} fill={C.gold} />
                <span className="text-[10px] font-semibold" style={{ color: C.gold }}>{listing.trust_score.toFixed(1)}</span>
              </div>
            )}
          </div>

          {/* Meta row */}
          <div className="flex items-center gap-2 text-[10px]" style={{ color: C.textMuted }}>
            <span className="flex items-center gap-0.5">
              <MapPin className="w-2.5 h-2.5" /> {listing.location || "Kampala"}
            </span>
            <span className="flex items-center gap-0.5">
              <Clock className="w-2.5 h-2.5" /> {timeAgo}
            </span>
            {listing.views != null && (
              <span className="flex items-center gap-0.5 ml-auto">
                <Eye className="w-2.5 h-2.5" /> {listing.views}
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// EDITORIAL FEATURED STORY — Apple App Store style
// ═══════════════════════════════════════════════════════════════════════════
function EditorialFeature({ listing, onSelect }: { listing: Listing; onSelect: () => void }) {
  const [isHovered, setIsHovered] = useState(false);
  const imageUrl = listing.image_urls?.[0];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      whileHover={{ y: -2 }}
      onClick={onSelect}
      className="relative overflow-hidden rounded-[24px] cursor-pointer shadow-premium"
      style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 12px 32px -8px rgba(0,0,0,0.1)" }}
    >
      <div className="relative h-72 sm:h-80 overflow-hidden">
        {imageUrl ? (
          <motion.img
            src={imageUrl}
            alt={listing.title}
            className="w-full h-full object-cover"
            animate={{ scale: isHovered ? 1.04 : 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          />
        ) : (
          <div className="h-full" style={{ background: "linear-gradient(135deg, #7B1416 0%, #A42022 100%)" }} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

        {/* Content overlay */}
        <div className="absolute inset-0 flex flex-col justify-end p-6 sm:p-8">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex items-center gap-2 mb-3"
          >
            <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-tight" style={{ background: "rgba(255,255,255,0.2)", backdropFilter: "blur(10px)", color: "white" }}>
              School Community Spotlight
            </span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-2xl sm:text-3xl font-semibold tracking-tight text-white mb-2 max-w-lg"
          >
            {listing.title}
          </motion.h2>
          {listing.description && (
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-sm sm:text-base text-white/80 max-w-md mb-4 line-clamp-2"
            >
              {listing.description}
            </motion.p>
          )}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex items-center gap-4"
          >
            <span className="text-xs text-white/70 flex items-center gap-1">
              <Users className="w-3.5 h-3.5" /> {listing.views ?? 460} families interested
            </span>
            <button className="text-xs font-semibold text-white flex items-center gap-1 px-4 py-2 rounded-full transition-all" style={{ background: "rgba(255,255,255,0.15)", backdropFilter: "blur(10px)" }}>
              Reserve a place <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// COMMUNITY CARD — for community sections
// ═══════════════════════════════════════════════════════════════════════════
function CommunityCard({
  icon: Icon,
  title,
  subtitle,
  accent,
  bg,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  title: string;
  subtitle: string;
  accent: string;
  bg: string;
  onClick?: () => void;
}) {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <motion.div
      whileHover={{ y: -2 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      onClick={onClick}
      className="flex items-center gap-3 p-4 rounded-[18px] border border-[#E2D6C0]/50 bg-white cursor-pointer transition-all"
      style={{ boxShadow: isHovered ? "0 8px 24px -6px rgba(0,0,0,0.08)" : "0 1px 3px rgba(0,0,0,0.03)" }}
    >
      <div className="h-10 w-10 rounded-[12px] flex items-center justify-center shrink-0" style={{ background: bg }}>
        <Icon className="h-5 w-5" style={{ color: accent }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold tracking-tight truncate" style={{ color: C.text }}>{title}</p>
        <p className="text-xs truncate" style={{ color: C.textMuted }}>{subtitle}</p>
      </div>
      <ChevronRight className="h-4 w-4 shrink-0" style={{ color: C.textLight }} />
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
export function ParentCampusExchange() {
  const { user } = useAuth();

  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
  const [savedItems, setSavedItems] = useState<Set<string>>(new Set());
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [sortBy, setSortBy] = useState<"newest" | "popular">("newest");

  const seasonal = useMemo(() => getSeasonalContext(), []);
  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  }, []);
  const firstName = (user?.user_metadata?.full_name ?? "Parent").split(" ")[0];

  // Fetch listings
  const { data: listings = [], isLoading } = useQuery<Listing[]>({
    queryKey: ["campus_exchange_listings", activeCategory, sortBy],
    queryFn: async () => {
      let query = supabase
        .from("marketplace_listings")
        .select("*, schools(name)")
        .eq("status", "active")
        .order("created_at", { ascending: sortBy === "newest" });

      if (activeCategory) query = query.eq("category", activeCategory);

      const { data, error } = await query.limit(50);
      if (error) throw error;
      return (data ?? []).map((item: any) => ({
        ...item,
        trust_score: 4.5 + Math.random() * 0.5,
        verified: Math.random() > 0.3,
        school_badge: item.posted_by_role === "school",
      }));
    },
  });

  // Demo listings — editorial, community, marketplace
  const demoListings: Listing[] = [
    {
      id: "demo-feature",
      title: "Annual Science Fair 2026",
      description: "TriSphere Academy presents the Annual Science Fair — students showcase innovative projects, robotics, and experiments.",
      category: "events",
      location: "TriSphere Academy Main Hall",
      posted_by_name: "TriSphere Academy",
      posted_by_role: "school",
      created_at: new Date(Date.now() - 259200000).toISOString(),
      image_urls: ["https://images.pexels.com/photos/2280571/pexels-photo-2280571.jpeg?auto=compress&cs=tinysrgb&w=800"],
      featured: true,
      school_badge: true,
      views: 460,
      cross_campus: true,
    },
    {
      id: "demo-1",
      title: "Complete Senior Chemistry Textbook Set",
      description: "Like new condition — Senior 1-6 Chemistry textbooks with revision guides and past papers.",
      price: 150000,
      category: "books",
      condition: "Like New",
      location: "Nakasero",
      posted_by_name: "Jennifer M.",
      posted_by_role: "parent",
      created_at: new Date(Date.now() - 86400000).toISOString(),
      image_urls: ["https://images.pexels.com/photos/1595385/pexels-photo-1595385.jpeg?auto=compress&cs=tinysrgb&w=600"],
      featured: true,
      verified: true,
      views: 234,
      trust_score: 4.8,
    },
    {
      id: "demo-2",
      title: "Private Mathematics Tutoring — A-Level",
      description: "Experienced teacher with 10+ years offering personalized tutoring. First session free!",
      price: 50000,
      price_unit: "UGX/hour",
      category: "tutoring",
      location: "Kololo",
      posted_by_name: "Mr. Okumu",
      posted_by_role: "parent",
      created_at: new Date(Date.now() - 172800000).toISOString(),
      image_urls: ["https://images.pexels.com/photos/5212370/pexels-photo-5212370.jpeg?auto=compress&cs=tinysrgb&w=600"],
      verified: true,
      views: 567,
      trust_score: 4.9,
    },
    {
      id: "demo-3",
      title: "LOST: Blue Backpack with Books",
      description: "Lost near main gate — blue backpack with S4 Math textbook and calculator.",
      category: "lost",
      location: "Main Gate",
      posted_by_name: "Michael A.",
      posted_by_role: "student",
      created_at: new Date(Date.now() - 43200000).toISOString(),
      contact_phone: "+256 772 222333",
      urgent: true,
      views: 956,
    },
    {
      id: "demo-4",
      title: "Football Boots — Size 6, Barely Used",
      price: 75000,
      category: "sports",
      condition: "Good",
      location: "Naguru",
      posted_by_name: "Sarah K.",
      posted_by_role: "parent",
      created_at: new Date(Date.now() - 129600000).toISOString(),
      image_urls: ["https://images.pexels.com/photos/270348/pexels-photo-270348.jpeg?auto=compress&cs=tinysrgb&w=600"],
      verified: true,
      views: 142,
      trust_score: 4.7,
    },
    {
      id: "demo-5",
      title: "Free: Nursery Uniforms (3 pieces)",
      category: "community",
      location: "Bugolobi",
      posted_by_name: "Grace N.",
      posted_by_role: "parent",
      created_at: new Date(Date.now() - 216000000).toISOString(),
      verified: true,
      views: 89,
      trust_score: 4.6,
    },
    {
      id: "demo-6",
      title: "Music Lessons — Piano & Guitar",
      price: 40000,
      price_unit: "UGX/session",
      category: "instruments",
      location: "Bukoto",
      posted_by_name: "Teacher David",
      posted_by_role: "parent",
      created_at: new Date(Date.now() - 302400000).toISOString(),
      image_urls: ["https://images.pexels.com/photos/1407322/pexels-photo-1407322.jpeg?auto=compress&cs=tinysrgb&w=600"],
      views: 312,
      trust_score: 4.8,
    },
    {
      id: "demo-7",
      title: "School Trip — Source of the Nile",
      category: "events",
      location: "Jinja Road",
      posted_by_name: "Greenhill Academy",
      posted_by_role: "school",
      created_at: new Date(Date.now() - 345600000).toISOString(),
      image_urls: ["https://images.pexels.com/photos/3601425/pexels-photo-3601425.jpeg?auto=compress&cs=tinysrgb&w=600"],
      school_badge: true,
      views: 780,
    },
    {
      id: "demo-8",
      title: "Science Kit — Microscope & Slides",
      price: 200000,
      category: "supplies",
      condition: "Excellent",
      location: "Kisaasi",
      posted_by_name: "Robert T.",
      posted_by_role: "parent",
      created_at: new Date(Date.now() - 432000000).toISOString(),
      image_urls: ["https://images.pexels.com/photos/2280571/pexels-photo-2280571.jpeg?auto=compress&cs=tinysrgb&w=600"],
      verified: true,
      views: 203,
      trust_score: 4.7,
    },
  ];

  const allListings = useMemo(() => {
    let items = [...demoListings, ...listings];
    if (activeCategory) items = items.filter((l) => l.category === activeCategory);
    if (searchQuery) {
      items = items.filter(
        (l) =>
          l.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          l.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    if (sortBy === "popular") items = [...items].sort((a, b) => (b.views ?? 0) - (a.views ?? 0));
    return items;
  }, [demoListings, listings, activeCategory, searchQuery, sortBy]);

  const featureListing = demoListings.find((l) => l.id === "demo-feature") ?? demoListings[0];
  const gridListings = allListings.filter((l) => l.id !== featureListing.id);
  const trendingListings = allListings.filter((l) => l.featured || (l.views ?? 0) > 400).slice(0, 6);

  const toggleSave = (id: string) => {
    setSavedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    toast.success(savedItems.has(id) ? "Removed from saved" : "Saved to favorites");
  };

  // Community feed items
  const communityFeed = [
    { icon: HeartHandshake, title: "Parents giving away nursery uniforms", subtitle: "3 families · Bugolobi", accent: C.green, bg: C.greenLight },
    { icon: GraduationCap, title: "3 new mathematics tutors joined today", subtitle: "Available this week", accent: C.teal, bg: C.tealLight },
    { icon: AlertTriangle, title: "Lost iPad near the science lab", subtitle: "Posted 2 hours ago", accent: C.red, bg: C.redLight },
    { icon: HeartHandshake, title: "PTA is organizing a charity walk", subtitle: "Saturday · 42 signed up", accent: C.pink, bg: C.pinkLight },
    { icon: Trophy, title: "Basketball coach available weekends", subtitle: "Coach Patrick · Kololo", accent: C.orange, bg: C.orangeLight },
    { icon: Calendar, title: "Greenhill School Fair registrations close tomorrow", subtitle: "460 families attending", accent: C.purple, bg: C.purpleLight },
  ];

  return (
    <div className="min-h-screen -mx-4 sm:-mx-6 lg:-mx-8 -mt-4 sm:-mt-6 lg:-mt-8" style={{ background: C.bg }}>
      {/* ═══════════════════════════════════════════════════════════════
          DISCOVERY CANVAS — replaces the hero entirely
      ═══════════════════════════════════════════════════════════════ */}
      <div className="px-4 sm:px-6 lg:px-8 pt-2 pb-4">
        {/* Greeting line — editorial, minimal */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-5"
        >
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" style={{ color: C.text }}>
            {greeting}, {firstName}.
          </h1>
          <p className="text-sm mt-1" style={{ color: C.textMuted }}>
            Your school community is active · {seasonal.label}
          </p>
        </motion.div>

        {/* Spotlight search — Apple Spotlight style */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.4 }}
          className="relative mb-5"
        >
          <div
            className="flex items-center gap-3 rounded-[16px] px-4 py-3.5 border transition-all"
            style={{
              background: isSearchFocused ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.72)",
              backdropFilter: "blur(40px) saturate(180%)",
              borderColor: isSearchFocused ? `${C.accent}40` : `${C.border}60`,
              boxShadow: isSearchFocused ? "0 8px 32px -8px rgba(0,113,227,0.15)" : "0 1px 3px rgba(0,0,0,0.04)",
            }}
          >
            <Search className="h-5 w-5 shrink-0" style={{ color: isSearchFocused ? C.accent : C.textMuted }} />
            <input
              type="text"
              placeholder="Search anything — textbooks, tutors, events, lost items…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-[#8B6F47]"
              style={{ color: C.text }}
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")} className="p-1 rounded-full hover:bg-[#EDE4D4]">
                <X className="h-4 w-4" style={{ color: C.textMuted }} />
              </button>
            )}
            <kbd className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-[#E2D6C0] bg-white shrink-0" style={{ color: C.textMuted }}>
              ⌘K
            </kbd>
          </div>
        </motion.div>

        {/* Floating category chips — intelligent discovery */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.4 }}
          className="flex flex-wrap gap-2 mb-6"
        >
          <button
            onClick={() => setActiveCategory(null)}
            className="shrink-0 px-3.5 py-2 rounded-full text-xs font-semibold tracking-tight transition-all"
            style={{
              background: !activeCategory ? C.text : "rgba(255,255,255,0.8)",
              color: !activeCategory ? "white" : C.textMid,
              backdropFilter: !activeCategory ? "none" : "blur(20px)",
              border: `1px solid ${!activeCategory ? C.text : C.border}`,
            }}
          >
            All
          </button>
          {CATEGORIES.map((cat) => {
            const isActive = activeCategory === cat.id;
            const Icon = cat.icon;
            return (
              <motion.button
                key={cat.id}
                whileTap={{ scale: 0.95 }}
                onClick={() => setActiveCategory(isActive ? null : cat.id)}
                className="shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold tracking-tight transition-all"
                style={{
                  background: isActive ? cat.color : "rgba(255,255,255,0.8)",
                  color: isActive ? "white" : C.textMid,
                  backdropFilter: isActive ? "none" : "blur(20px)",
                  border: `1px solid ${isActive ? cat.color : C.border}`,
                }}
              >
                <Icon className="h-3.5 w-3.5" />
                {cat.label}
              </motion.button>
            );
          })}
        </motion.div>

        {/* Editorial Featured Story — replaces hero */}
        {!searchQuery && !activeCategory && (
          <div className="mb-6">
            <EditorialFeature listing={featureListing} onSelect={() => setSelectedListing(featureListing)} />
          </div>
        )}

        {/* Integrated statistics — typography as visualization */}
        {!searchQuery && !activeCategory && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mb-6"
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 py-4 px-1">
              <p className="text-sm" style={{ color: C.textMid }}>
                <span className="text-xl font-semibold tracking-tight" style={{ color: C.text }}>2,847</span> active opportunities across your school community
              </p>
              <span className="hidden sm:inline text-[#E2D6C0]">·</span>
              <p className="text-sm" style={{ color: C.textMid }}>
                <span className="text-xl font-semibold tracking-tight" style={{ color: C.text }}>14,200</span> verified families connected
              </p>
              <span className="hidden sm:inline text-[#E2D6C0]">·</span>
              <p className="text-sm" style={{ color: C.textMid }}>
                <span className="text-xl font-semibold tracking-tight" style={{ color: C.text }}>1,234</span> trusted parents and teachers sharing
              </p>
            </div>
          </motion.div>
        )}

        {/* Trending strip — horizontal scroll */}
        {!searchQuery && !activeCategory && trendingListings.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Flame className="h-4 w-4" style={{ color: C.orange }} />
                <h2 className="text-base font-semibold tracking-tight" style={{ color: C.text }}>Popular This Week</h2>
              </div>
              <button className="text-xs font-medium flex items-center gap-0.5" style={{ color: C.accent }}>
                See all <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 no-scrollbar">
              {trendingListings.map((item) => {
                const cat = CATEGORIES.find((c) => c.id === item.category) ?? CATEGORIES[0];
                const imageUrl = item.image_urls?.[0];
                return (
                  <motion.div
                    key={item.id}
                    whileHover={{ y: -3 }}
                    onClick={() => setSelectedListing(item)}
                    className="shrink-0 w-44 cursor-pointer"
                  >
                    <div className="rounded-[16px] overflow-hidden border border-[#E2D6C0]/50 shadow-premium bg-white">
                      <div className="relative h-28 overflow-hidden">
                        {imageUrl ? (
                          <img src={imageUrl} alt={item.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="h-full flex items-center justify-center" style={{ background: cat.bg }}>
                            <cat.icon className="h-8 w-8" style={{ color: cat.color, opacity: 0.5 }} />
                          </div>
                        )}
                        {item.featured && (
                          <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-md text-[9px] font-semibold text-white flex items-center gap-0.5" style={{ background: C.accent }}>
                            <Zap className="w-2 h-2" /> Hot
                          </span>
                        )}
                      </div>
                      <div className="p-2.5">
                        <h4 className="text-xs font-semibold truncate tracking-tight" style={{ color: C.text }}>{item.title}</h4>
                        <p className="text-[10px] font-semibold mt-0.5" style={{ color: cat.color }}>
                          {item.price ? `${item.price.toLocaleString()} UGX` : "Free"}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          DISCOVERY FEED — Pinterest masonry grid
      ═══════════════════════════════════════════════════════════════ */}
      <div className="px-4 sm:px-6 lg:px-8 pb-6">
        {/* Section header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold tracking-tight" style={{ color: C.text }}>
            {searchQuery
              ? `Results for "${searchQuery}"`
              : activeCategory
                ? CATEGORIES.find((c) => c.id === activeCategory)?.label
                : "Discover Your Community"}
          </h2>
          <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: C.textMuted }}>{gridListings.length} listings</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="text-xs rounded-lg border border-[#E2D6C0] bg-white px-2.5 py-1.5 outline-none"
              style={{ color: C.textMid }}
            >
              <option value="newest">Newest</option>
              <option value="popular">Most Popular</option>
            </select>
          </div>
        </div>

        {/* Masonry grid */}
        {isLoading ? (
          <div className="columns-2 sm:columns-3 lg:columns-4 xl:columns-5 gap-4">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="break-inside-avoid mb-4 rounded-[20px] overflow-hidden animate-pulse" style={{ background: C.card }}>
                <div className="h-48" style={{ background: C.border }} />
                <div className="p-3.5 space-y-2">
                  <div className="h-3 rounded w-3/4" style={{ background: C.border }} />
                  <div className="h-2.5 rounded w-1/2" style={{ background: C.border }} />
                </div>
              </div>
            ))}
          </div>
        ) : gridListings.length === 0 ? (
          <div className="text-center py-20">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring" }}
              className="w-20 h-20 rounded-[24px] mx-auto mb-5 flex items-center justify-center"
              style={{ background: C.accentLight }}
            >
              <Search className="h-10 w-10" style={{ color: C.accent }} />
            </motion.div>
            <h3 className="text-lg font-semibold mb-2 tracking-tight" style={{ color: C.text }}>
              {searchQuery ? "Nothing found" : "No listings yet"}
            </h3>
            <p className="text-sm mb-5" style={{ color: C.textMuted }}>
              {searchQuery ? "Try a different search term" : "Be the first to share something with your community"}
            </p>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-white"
              style={{ background: "linear-gradient(135deg, #7B1416, #A42022)" }}
            >
              <Plus className="h-4 w-4" /> Post Listing
            </motion.button>
          </div>
        ) : (
          <div className="columns-2 sm:columns-3 lg:columns-4 xl:columns-5 gap-4">
            {gridListings.map((listing, idx) => (
              <DiscoveryCard
                key={listing.id}
                listing={listing}
                index={idx}
                onSelect={() => setSelectedListing(listing)}
                saved={savedItems.has(listing.id)}
                onToggleSave={() => toggleSave(listing.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          COMMUNITY LAYER — belonging, not e-commerce
      ═══════════════════════════════════════════════════════════════ */}
      {!searchQuery && !activeCategory && (
        <div className="px-4 sm:px-6 lg:px-8 pb-8">
          <div className="flex items-center gap-2 mb-4">
            <HeartHandshake className="h-4 w-4" style={{ color: C.green }} />
            <h2 className="text-lg font-semibold tracking-tight" style={{ color: C.text }}>Community Pulse</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {communityFeed.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.06, duration: 0.3 }}
              >
                <CommunityCard {...item} />
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          DETAIL MODAL — refined, Apple-style
      ═══════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {selectedListing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(16px)" }}
            onClick={() => setSelectedListing(null)}
          >
            <motion.div
              initial={{ scale: 0.92, y: 24, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.92, y: 24, opacity: 0 }}
              transition={{ type: "spring", stiffness: 400, damping: 32 }}
              className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-[24px] bg-white shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {selectedListing.image_urls?.[0] && (
                <div className="relative h-64 sm:h-72 overflow-hidden">
                  <img src={selectedListing.image_urls[0]} alt={selectedListing.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  <button
                    onClick={() => setSelectedListing(null)}
                    className="absolute top-4 right-4 p-2.5 rounded-full bg-white/90 backdrop-blur-md shadow-lg transition-all hover:bg-white"
                  >
                    <X className="h-5 w-5" style={{ color: C.text }} />
                  </button>
                </div>
              )}
              <div className="p-6">
                <h2 className="text-xl font-semibold tracking-tight mb-3" style={{ color: C.text }}>{selectedListing.title}</h2>
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold text-white"
                    style={{ background: `linear-gradient(135deg, ${CATEGORIES.find((c) => c.id === selectedListing.category)?.color ?? C.accent}, ${CATEGORIES.find((c) => c.id === selectedListing.category)?.color ?? C.accent}cc)` }}
                  >
                    {selectedListing.posted_by_name?.charAt(0) || "P"}
                  </div>
                  <div>
                    <p className="text-sm font-medium" style={{ color: C.text }}>{selectedListing.posted_by_name}</p>
                    <p className="text-xs capitalize" style={{ color: C.textMuted }}>{selectedListing.posted_by_role}</p>
                  </div>
                  {selectedListing.verified && <BadgeCheck className="w-4 h-4" style={{ color: C.green }} />}
                </div>
                {selectedListing.description && (
                  <p className="text-sm leading-relaxed mb-4" style={{ color: C.textMid }}>{selectedListing.description}</p>
                )}
                <div className="flex gap-3">
                  {selectedListing.contact_phone && (
                    <a href={`tel:${selectedListing.contact_phone}`} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-[14px] text-white text-sm font-semibold" style={{ background: C.green }}>
                      <Phone className="w-4 h-4" /> Call
                    </a>
                  )}
                  <button className="flex-1 flex items-center justify-center gap-2 py-3 rounded-[14px] text-sm font-semibold text-white" style={{ background: C.accent }}>
                    <MessageCircle className="w-4 h-4" /> Message
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default ParentCampusExchange;
